import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PinButton, PinNotice, useSectionPins } from "@/components/ItemPins";
import { ItineraryExports } from "@/components/ItineraryExports";
import { useDndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { ActivityContentTarget, ActivityInsertionTarget, ActivityPlaceChips, ItineraryActivityDrag, ItinerarySavedPlaces, ItineraryPlaceDetails, SavedPlaceDragArea, matchingSavedPlaces } from "@/components/ItinerarySavedPlaces";
import api, { fileUrl } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { toSentenceCase } from "@/lib/utils";
import { Plus, MapPin, Pencil, Trash2, ArrowLeft, Plane, Clock, Search, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const uid = () => Math.random().toString(36).slice(2, 10);
const hasActivityContent = (activity) => Boolean(activity.title.trim() || activity.save_id || activity.place_links?.length);
const containsPlace = (activity, saveId) => activity.save_id === saveId || activity.place_links?.some((link) => link.save_id === saveId);
const emptyTrip = { title: "", destination: "", start_date: "", number_of_days: "", image_path: null };
const emptyStartDate = { day: "", month: "", year: "" };
const parseTripStartDate = ({ day, month, year }) => {
  if (!day && !month && !year) return "";
  if (!day || !month || !year) return null;
  return tripDayDate(`${year}-${month}-${day}`, 0) || null;
};
const tripDayDate = (startDate, offset) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate || "")) return "";
  const parsed = new Date(`${startDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== startDate) return "";
  parsed.setUTCDate(parsed.getUTCDate() + offset);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
};
const getStartDateParts = (value) => {
  const iso = tripDayDate(value, 0);
  return iso ? { day: iso.slice(8, 10), month: iso.slice(5, 7), year: iso.slice(0, 4) } : { ...emptyStartDate };
};
const formatTripStartDate = (value) => {
  const iso = tripDayDate(value, 0);
  return iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(2, 4)}` : value || "";
};
const formatTripDayDate = (value, weekdayStyle = "long") => {
  const iso = tripDayDate(value, 0);
  if (!iso) return value;
  const weekday = new Intl.DateTimeFormat("en-GB", { weekday: weekdayStyle, timeZone: "UTC" }).format(new Date(`${iso}T00:00:00Z`));
  return `${weekday} ${formatTripStartDate(iso)}`;
};
const formatTripDuration = (dayCount) => {
  const nightCount = Math.max(dayCount - 1, 0);
  return `${dayCount} ${dayCount === 1 ? "day" : "days"} - ${nightCount} ${nightCount === 1 ? "night" : "nights"}`;
};

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TripStartDateField = ({ value, onChange, prefix, disabled = false, inputClassName = "", labelClassName = "" }) => {
  const hasValue = Boolean(value.day || value.month || value.year);
  const incomplete = hasValue && (!value.day || !value.month || !value.year);
  const message = incomplete ? "Complete Day, Month and Year, or clear the date."
    : parseTripStartDate(value) === null ? "Choose a valid day for the selected month and year." : "";
  const years = Array.from({ length: 74 }, (_, index) => String(2026 + index));
  if (value.year && !years.includes(value.year)) years.push(value.year);
  const fields = [
    { name: "day", label: "Day", options: Array.from({ length: 31 }, (_, index) => { const day = String(index + 1).padStart(2, "0"); return [day, day]; }) },
    { name: "month", label: "Month", options: monthNames.map((name, index) => [String(index + 1).padStart(2, "0"), name]) },
    { name: "year", label: "Year", options: years.map((year) => [year, year]) },
  ];
  return (
    <div data-testid={`${prefix}-field`} role="group" aria-labelledby={`${prefix}-label`} className="min-w-0 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label id={`${prefix}-label`} data-testid={`${prefix}-label`} htmlFor={`${prefix}-day`} className={labelClassName}>Start date</Label>
        {hasValue && <button type="button" data-testid={`${prefix}-clear`} disabled={disabled} onClick={() => onChange({ ...emptyStartDate })} className="rounded text-xs font-semibold text-stone-500 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 transition-colors">Clear date</button>}
      </div>
      <div className="grid grid-cols-3 gap-2 min-w-0">
        {fields.map((field) => <Select key={field.name} value={value[field.name]} disabled={disabled} onValueChange={(selected) => onChange({ ...value, [field.name]: selected })}>
          <SelectTrigger id={`${prefix}-${field.name}`} data-testid={`${prefix}-${field.name}`} aria-label={`Start date ${field.name}`} aria-required={hasValue} aria-invalid={!!message} aria-describedby={message ? `${prefix}-error` : undefined} className={`min-w-0 rounded-xl px-2.5 ${inputClassName}`}>
            <SelectValue placeholder={field.label}>{value[field.name] ? field.name === "month" ? monthNames[Number(value.month) - 1].slice(0, 3) : value[field.name] : undefined}</SelectValue>
          </SelectTrigger>
          <SelectContent data-testid={`${prefix}-${field.name}-options`} align={field.name === "year" ? "end" : "start"} collisionPadding={12} className="max-h-64 rounded-xl bg-white">
            {field.options.map(([option, label]) => <SelectItem key={option} value={option} data-testid={`${prefix}-${field.name}-option-${option}`}>{label}</SelectItem>)}
          </SelectContent>
        </Select>)}
      </div>
      {message && <p id={`${prefix}-error`} data-testid={`${prefix}-error`} role={incomplete ? "status" : "alert"} aria-live="polite" className={`text-sm ${incomplete ? "text-stone-500" : "text-rose-600"}`}>{message}</p>}
    </div>
  );
};

export const Itinerary = ({ refreshKey, resumedSave }) => {
  const [trips, setTrips] = useState([]);
  const [saves, setSaves] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyTrip);
  const [daysError, setDaysError] = useState("");
  const [startDateParts, setStartDateParts] = useState(emptyStartDate);
  const daysInputRef = useRef(null);
  const [query, setQuery] = useState("");
  const pins = useSectionPins("trips", refreshKey);
  const q = query.trim().toLowerCase();
  const shown = [...(!q ? trips : trips.filter((trip) => [trip.title, trip.destination].filter(Boolean).join(" ").toLowerCase().includes(q)))].sort((a, b) => Number(pins.ids.includes(b.id)) - Number(pins.ids.includes(a.id)));

  const load = async () => {
    const res = await api.get("/trips");
    setTrips(res.data);
    if (selected) {
      const upd = res.data.find((t) => t.id === selected.id);
      setSelected(upd || null);
    }
  };
  const loadSaves = async () => setSaves((await api.get("/saves")).data);
  useEffect(() => { load(); loadSaves(); }, [refreshKey]);
  const resumed = useRef(null);
  useEffect(() => {
    if (resumedSave && resumed.current !== resumedSave) {
      const trip = trips.find((item) => item.id === resumedSave);
      if (trip) { resumed.current = resumedSave; setSelected(trip); }
    }
  }, [resumedSave, trips]);

  const create = async () => {
    if (!form.title.trim()) return;
    const startDate = parseTripStartDate(startDateParts);
    if (startDate === null) return;
    const numberOfDays = form.number_of_days.trim() ? Number(form.number_of_days) : null;
    if (daysInputRef.current?.validity.badInput || (numberOfDays !== null && (!Number.isInteger(numberOfDays) || numberOfDays < 1 || numberOfDays > 365))) {
      setDaysError("Enter a whole number from 1 to 365, or leave this blank.");
      return;
    }
    setDaysError("");
    await api.post("/trips", { ...form, title: form.title.trim(), destination: form.destination.trim(), start_date: startDate, number_of_days: numberOfDays });
    setForm(emptyTrip); setStartDateParts({ ...emptyStartDate });
    setOpen(false);
    load();
  };

  const removeTrip = async (id) => {
    await api.delete(`/trips/${id}`);
    pins.removeDeleted(id);
    setSelected(null);
    load();
  };

  const saveDays = async (trip, days) => {
    const res = await api.put(`/trips/${trip.id}`, { days });
    setSelected(res.data);
    load();
  };

  const saveTrip = async (tripId, changes) => {
    const { data } = await api.put(`/trips/${tripId}`, changes);
    setTrips((current) => current.map((trip) => trip.id === data.id ? data : trip));
    setSelected(data);
  };

  if (selected) {
    return (
      <div data-testid="itinerary-detail-panel" className="rounded-3xl bg-white/85 backdrop-blur-xl border border-white/60 shadow-2xl p-5 md:p-8">
        <TripDetail
          trip={selected}
          saves={saves}
          onBack={() => setSelected(null)}
          onSaveDays={saveDays}
          onSaveTrip={saveTrip}
          onDelete={removeTrip}
        />
      </div>
    );
  }

  return (
    <div data-testid="itinerary-panel" className="mobile-section-inset space-y-4 border border-transparent p-5 md:p-8">
      <PinNotice section="trips" pins={pins} />
      <div data-testid="itinerary-header" className="mobile-section-header flex items-end justify-between gap-3 w-full px-5 py-4 rounded-3xl bg-white/95 backdrop-blur-md border border-white/60 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <div className="min-w-0">
          <h2 data-testid="itinerary-heading" className="text-2xl sm:text-3xl font-bold font-['Nunito'] text-stone-900 tracking-tight">Itineraries</h2>
          <p data-testid="itinerary-tagline" className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400 mt-1">Day-by-day plans</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-trip-btn" size="icon" aria-label="New trip" title="New trip" className="ml-auto self-start -mt-0.5 sm:mt-0 shrink-0 rounded-full bg-ac hover:bg-ac-hover text-white hover:-translate-y-0.5 transition-[transform,background-color]">
              <Plus aria-hidden="true" className="w-5 h-5" strokeWidth={3} />
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl font-['Figtree'] max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle data-testid="new-trip-dialog-title" className="font-['Nunito'] text-2xl">New trip</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <ImageUpload value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} testid="trip-image" />
              <div className="relative">
                <Input data-testid="trip-title-input" aria-label="Trip name" aria-required="true" placeholder="Trip name (e.g. Summer in Italy)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl placeholder:text-transparent" />
                {!form.title && <span data-testid="trip-title-placeholder" aria-hidden="true" className="pointer-events-none absolute left-[13px] right-3 top-1/2 -translate-y-1/2 flex min-w-0 items-baseline text-base md:text-sm text-muted-foreground"><span className="truncate">Trip name (e.g. Summer in Italy)</span><span data-testid="trip-title-required" className="shrink-0 text-red-600">*</span></span>}
              </div>
              <Input data-testid="trip-destination-input" placeholder="Places" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="rounded-xl" />
              <TripStartDateField value={startDateParts} onChange={setStartDateParts} prefix="trip-start" labelClassName="text-muted-foreground" />
              <div>
                <Input ref={daysInputRef} data-testid="trip-days-input" type="number" min="1" max="365" step="1" inputMode="numeric" aria-label="No. of days (optional)" aria-invalid={!!daysError} aria-describedby={daysError ? "trip-days-error" : undefined} placeholder="No. of days" value={form.number_of_days} onChange={(e) => { setForm({ ...form, number_of_days: e.target.value }); setDaysError(""); }} className="rounded-xl" />
                {daysError && <p id="trip-days-error" data-testid="trip-days-error" role="alert" className="mt-2 text-sm text-rose-600">{daysError}</p>}
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-trip-btn" onClick={create} disabled={parseTripStartDate(startDateParts) === null} className="rounded-full bg-ac hover:bg-ac-hover font-bold w-full">Create trip</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div data-testid="itinerary-search-panel" className="relative w-full rounded-3xl bg-white/95 backdrop-blur-md border border-white/60 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <Search aria-hidden="true" className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
        <Input data-testid="itinerary-search-input" aria-label="Search trips by name or destination" placeholder="Search by name or destination" value={query} onChange={(event) => setQuery(event.target.value)} className="rounded-3xl pl-12 pr-10 h-14 bg-transparent border-0 shadow-none text-sm" />
        {query && <button type="button" data-testid="itinerary-search-clear" aria-label="Clear search" title="Clear search" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors"><X aria-hidden="true" className="w-4 h-4" /></button>}
      </div>

      {shown.length === 0 ? (
        <div data-testid="itinerary-empty-state" role="status" className="w-full rounded-3xl border border-white/60 bg-white/95 backdrop-blur-md shadow-[0_8px_24px_rgba(0,0,0,0.14)] py-20 flex flex-col items-center text-center px-6">
          <span className="w-14 h-14 rounded-full bg-sky-100 text-sky-600 grid place-items-center mb-4"><Plane className="w-7 h-7" strokeWidth={2.5} /></span>
          <h3 data-testid="itinerary-empty-title" className="text-xl font-bold font-['Nunito'] text-stone-900">{q ? "No matches" : "No trips planned"}</h3>
          <p data-testid="itinerary-empty-message" className="text-stone-500 mt-1 max-w-xs">{q ? "Try a different trip name or destination." : "Create a trip and build a day-by-day itinerary."}</p>
        </div>
      ) : (
        <div data-testid="itinerary-cards" className="flex flex-col gap-4 w-full">
          {shown.map((trip, i) => {
            const dayCount = trip.days?.length || 0;
            return (
              <motion.div
                key={trip.id}
                data-testid={`trip-card-${trip.id}`}
                data-pinned={pins.ids.includes(trip.id)}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="relative w-full min-w-0 text-left rounded-3xl overflow-hidden border border-white/60 bg-white/95 backdrop-blur-md shadow-[0_8px_24px_rgba(0,0,0,0.14)] hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300"
              >
                <button type="button" data-testid={`open-trip-${trip.id}`} aria-label={`Open ${trip.title}`} onClick={() => setSelected(trip)} className="absolute inset-0 z-[1] rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring" />
                {trip.image_path && <div className="relative h-40">
                  <img data-testid={`trip-card-photo-${trip.id}`} src={fileUrl(trip.image_path)} alt={trip.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent" />
                </div>}
                <div className="min-w-0 p-5">
                  <div className="itinerary-card-heading flex items-baseline justify-between gap-3">
                    <h3 data-testid={`trip-card-title-${trip.id}`} className="min-w-0 flex-1 text-lg font-bold font-['Nunito'] text-stone-900 [overflow-wrap:anywhere]">{trip.title}</h3>
                    <span data-testid={`trip-card-duration-${trip.id}`} className="shrink-0 whitespace-nowrap text-xs sm:text-sm font-medium text-stone-500">{formatTripDuration(dayCount)}</span>
                    <PinButton section="trips" item={trip} pins={pins} />
                  </div>
                  {trip.destination && <p data-testid={`trip-card-destination-${trip.id}`} className="flex items-start gap-1 text-stone-500 text-sm mt-1 min-w-0"><MapPin aria-hidden="true" className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span className="min-w-0 [overflow-wrap:anywhere]">{trip.destination}</span></p>}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TripDetail = ({ trip, saves, onBack, onSaveDays, onSaveTrip, onDelete }) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [deletion, setDeletion] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [openedPlace, setOpenedPlace] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [placeStatus, setPlaceStatus] = useState(null);
  const [revealedActivity, setRevealedActivity] = useState(null);
  const [pendingEntries, setPendingEntries] = useState({});
  const [clearedPlaceActivityIds, setClearedPlaceActivityIds] = useState([]);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [reorderError, setReorderError] = useState("");
  const [addingActivity, setAddingActivity] = useState(false);
  const reorderPending = useRef(false);
  const updatePendingEntries = (dayId, update) => setPendingEntries((current) => ({ ...current, [dayId]: update(current[dayId] || []) }));
  const deleteTrigger = useRef(null);
  const [startDateParts, setStartDateParts] = useState(emptyStartDate);
  const baseline = useRef(null);
  const editing = draft !== null;
  const busy = saving || uploading || deleting || placing || reordering || addingActivity;
  const days = pendingOrder || (editing ? draft.days : trip.days || []);
  const allowedEmptyActivityIds = [...new Set([...clearedPlaceActivityIds, ...(baseline.current?.days || []).flatMap((day) => day.activities.filter((activity) => !hasActivityContent(activity)).map((activity) => activity.id))])];
  const excludedSaveIds = (editing ? draft.excluded_save_ids : trip.excluded_save_ids) || [];
  const matchedPlaces = matchingSavedPlaces(saves || [], editing ? draft.title : trip.title, editing ? draft.destination : trip.destination, excludedSaveIds);

  useEffect(() => {
    if (!revealedActivity) return;
    const frame = requestAnimationFrame(() => {
      const element = document.getElementById(revealedActivity.entryId ? `itinerary-entry-${revealedActivity.entryId}` : revealedActivity.id ? `itinerary-activity-${revealedActivity.id}` : `itinerary-place-status-${revealedActivity.dayId}`);
      element?.scrollIntoView({ block: "center", behavior: "smooth" });
      const focusTarget = revealedActivity.entryId ? element?.querySelector('input[aria-label="New activity text"]') : element;
      focusTarget?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [revealedActivity]);

  const beginEdit = () => {
    baseline.current = trip;
    setDraft({ title: trip.title, destination: trip.destination || "", start_date: trip.start_date || "", image_path: trip.image_path || null, days: trip.days || [], excluded_save_ids: trip.excluded_save_ids || [] });
    setPlaceStatus(null); setRevealedActivity(null); setClearedPlaceActivityIds([]);
    setStartDateParts(getStartDateParts(trip.start_date));
    setError("");
    requestAnimationFrame(() => document.getElementById("edit-trip-title-input")?.focus());
  };
  const cancelEdit = () => {
    setDraft(null); setError(""); setPlaceStatus(null); setRevealedActivity(null); setPendingEntries({}); setClearedPlaceActivityIds([]); setStartDateParts({ ...emptyStartDate }); baseline.current = null;
    requestAnimationFrame(() => document.querySelector('[data-testid="edit-trip-btn"]')?.focus());
  };
  const resolveEditedStartDate = (parts) => {
    const original = getStartDateParts(baseline.current.start_date);
    return ["day", "month", "year"].every((field) => parts[field] === original[field])
      ? baseline.current.start_date || "" : parseTripStartDate(parts);
  };
  const changeStartDate = (parts) => {
    setStartDateParts(parts);
    const startDate = resolveEditedStartDate(parts);
    if (startDate === null) return;
    setDraft((current) => ({
      ...current,
      start_date: startDate,
      days: !startDate || tripDayDate(startDate, 0)
        ? current.days.map((day, index) => ({ ...day, date: startDate ? tripDayDate(startDate, index) : "" }))
        : current.days,
    }));
  };
  const changeDays = (nextDays) => {
    if (editing) setDraft((current) => ({ ...current, days: nextDays }));
    else return onSaveDays(trip, nextDays);
  };
  const reorderDays = async (source, target) => {
    if (busy || reorderPending.current) return;
    const from = days.find((day) => day.id === source.dayId);
    const to = days.find((day) => day.id === target.dayId);
    if (!from || !to) return;
    let nextDays;
    if (source.type === "day-content") {
      if (from.id === to.id) return;
      nextDays = days.map((day) => day.id === from.id ? { ...day, activities: to.activities } : day.id === to.id ? { ...day, activities: from.activities } : day);
    } else {
      if (from.id !== to.id || target.type !== "itinerary-reorder-activity") return;
      const fromIndex = from.activities.findIndex((activity) => activity.id === source.activityId);
      const toIndex = from.activities.findIndex((activity) => activity.id === target.activityId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
      nextDays = days.map((day) => day.id === from.id ? { ...day, activities: arrayMove(day.activities, fromIndex, toIndex) } : day);
    }
    reorderPending.current = true;
    setReordering(true); setReorderError(""); setRevealedActivity(null); setPlaceStatus(null);
    if (!editing) setPendingOrder(nextDays);
    try {
      await changeDays(nextDays);
      if (source.type === "day-content") setPendingEntries((current) => ({ ...current, [from.id]: current[to.id] || [], [to.id]: current[from.id] || [] }));
    } catch {
      setReorderError("Could not save the arrangement. Your previous order has been restored. Please try again.");
    } finally {
      setPendingOrder(null); setReordering(false); reorderPending.current = false;
    }
  };
  const refreshDayDates = (nextDays) => {
    const startDate = tripDayDate(editing ? draft.start_date : trip.start_date, 0);
    return startDate ? nextDays.map((day, index) => ({ ...day, date: tripDayDate(startDate, index) })) : nextDays;
  };
  const addDay = () => {
    const newDay = { id: uid(), date: "", label: `Day ${days.length + 1}`, activities: [] };
    changeDays(refreshDayDates([...days, newDay]));
  };
  const removeDay = (dayId) => {
    const remainingDays = days
      .filter((day) => day.id !== dayId)
      .map((day, index) => ({ ...day, label: `Day ${index + 1}` }));
    return changeDays(refreshDayDates(remainingDays));
  };
  const addActivity = async (dayId, activity) => {
    setAddingActivity(true);
    try { await changeDays(days.map((day) => day.id === dayId ? { ...day, activities: [...day.activities, activity] } : day)); }
    finally { setAddingActivity(false); }
  };
  const removeActivity = (dayId, actId) => {
    return changeDays(days.map((day) => day.id === dayId ? { ...day, activities: day.activities.filter((activity) => activity.id !== actId) } : day));
  };
  const editActivity = (dayId, actId, changes) => {
    changeDays(days.map((day) => day.id === dayId ? { ...day, activities: day.activities.map((activity) => activity.id === actId ? { ...activity, ...changes } : activity) } : day));
  };
  const placeOnDay = async (dayId, place, target) => {
    if (busy) return;
    const day = days.find((item) => item.id === dayId);
    if (!day) return;
    const existing = day.activities.find((activity) => containsPlace(activity, place.id));
    const pending = (pendingEntries[dayId] || []).find((entry) => containsPlace(entry, place.id));
    if (existing || pending) {
      setPlaceStatus({ dayId, error: false, text: `${place.title} is already in ${day.label}${pending && !existing ? " in an unsaved entry" : ""}.` });
      setRevealedActivity({ id: existing?.id, entryId: existing ? undefined : pending.id, dayId, kind: "duplicate" });
      return;
    }
    const link = { save_id: place.id, title: place.title };
    if (target.entryId) {
      updatePendingEntries(dayId, (entries) => entries.map((entry) => entry.id === target.entryId ? { ...entry, place_links: [...(entry.place_links || []), link] } : entry));
      setPlaceStatus({ dayId, error: false, text: `${place.title} added to your entry. Press Enter to keep it${editing ? " in your draft" : ""}.` });
      setRevealedActivity({ entryId: target.entryId, dayId, kind: "linked" });
      return;
    }
    const index = Math.max(0, Math.min(target.position ?? day.activities.length, day.activities.length));
    const activity = { id: uid(), time: "", title: "", description: "", place_links: [link] };
    const activities = target.activityId
      ? day.activities.map((item) => item.id === target.activityId ? { ...item, place_links: [...(item.place_links || []), link] } : item)
      : [...day.activities.slice(0, index), activity, ...day.activities.slice(index)];
    setPlacing(true); setRevealedActivity(null);
    setPlaceStatus({ dayId, error: false, text: `Adding ${place.title} to ${day.label}…` });
    try {
      await changeDays(days.map((item) => item.id === dayId ? { ...item, activities } : item));
      setPlaceStatus({ dayId, error: false, text: `${place.title} added to ${day.label}${editing ? " in your draft" : ""}.` });
      setRevealedActivity({ id: target.activityId || activity.id, dayId, kind: target.activityId ? "linked" : "added" });
    } catch {
      setPlaceStatus({ dayId, error: true, text: `Could not save ${place.title} in ${day.label}. Please try dragging it again.` });
      setRevealedActivity({ dayId, kind: "error" });
    } finally { setPlacing(false); }
  };
  const removeActivityPlace = async (dayId, activityId, saveId) => {
    if (busy) return;
    const day = days.find((item) => item.id === dayId);
    const activity = day.activities.find((item) => item.id === activityId);
    const placeName = activity.place_links?.find((link) => link.save_id === saveId)?.title || activity.title || "Saved place";
    const updated = { ...activity, place_links: (activity.place_links || []).filter((link) => link.save_id !== saveId) };
    if (updated.save_id === saveId) updated.save_id = null;
    setPlacing(true); setRevealedActivity(null);
    setPlaceStatus({ dayId, error: false, text: `Removing ${placeName}…` });
    try {
      await changeDays(days.map((item) => item.id === dayId ? { ...item, activities: item.activities.map((entry) => entry.id === activityId ? updated : entry) } : item));
      if (editing && !hasActivityContent(updated)) setClearedPlaceActivityIds((current) => [...new Set([...current, activityId])]);
      setPlaceStatus({ dayId, error: false, text: `${placeName} removed from this activity${editing ? " in your draft" : ""}.` });
      setRevealedActivity({ id: activityId, dayId, kind: "removed" });
    } catch {
      setPlaceStatus({ dayId, error: true, text: "Could not remove this saved place. Please try again." });
      setRevealedActivity({ dayId, kind: "error" });
    } finally { setPlacing(false); }
  };
  const removeSavedPlace = async (saveId) => {
    const excluded = [...new Set([...excludedSaveIds, saveId])];
    if (editing) setDraft((current) => ({ ...current, excluded_save_ids: excluded }));
    else await onSaveTrip(trip.id, { excluded_save_ids: excluded });
  };
  const requestDelete = (target) => {
    deleteTrigger.current = document.activeElement;
    setDeleteError("");
    setDeletion(target);
  };
  const confirmDelete = async () => {
    if (!deletion || deleting) return;
    setDeleting(true); setDeleteError("");
    try {
      if (deletion.type === "trip") await onDelete(trip.id);
      else if (deletion.type === "saved-place") await removeSavedPlace(deletion.saveId);
      else if (deletion.type === "day") await removeDay(deletion.dayId);
      else await removeActivity(deletion.dayId, deletion.activityId);
      setDeletion(null);
    } catch {
      setDeleteError(deletion.type === "saved-place" ? "Could not remove this place. Please try again." : `Could not delete this ${deletion.type}. Please try again.`);
    } finally {
      setDeleting(false);
    }
  };
  const restoreDeleteFocus = (event) => {
    event.preventDefault();
    if (deleteTrigger.current?.isConnected) deleteTrigger.current.focus();
    else document.querySelector('[data-testid="add-day-btn"], [data-testid="add-trip-btn"]')?.focus();
  };
  const save = async () => {
    if (busy) return;
    if (!draft.title.trim()) { setError("Enter a trip name."); return; }
    const startDate = resolveEditedStartDate(startDateParts);
    if (startDate === null) return;
    if (days.some((day) => day.activities.some((activity) => !hasActivityContent(activity) && !allowedEmptyActivityIds.includes(activity.id)))) {
      setError("Enter text for each activity, or remove any empty activities.");
      return;
    }
    const original = baseline.current;
    const originalActivities = new Map((original.days || []).flatMap((day) => day.activities.map((activity) => [activity.id, activity])));
    const nextDays = days.map((day) => ({ ...day, activities: day.activities.map((activity) =>
      originalActivities.get(activity.id)?.title === activity.title ? activity : { ...activity, title: toSentenceCase(activity.title, { preserveCapitalization: true }) }
    ) }));
    const changes = {};
    if (draft.title !== original.title) changes.title = draft.title.trim();
    if (draft.destination !== (original.destination || "")) changes.destination = draft.destination.trim();
    if (startDate !== (original.start_date || "")) changes.start_date = startDate;
    if (draft.image_path !== (original.image_path || null)) changes.image_path = draft.image_path;
    if (JSON.stringify(nextDays) !== JSON.stringify(original.days || [])) changes.days = nextDays;
    if (JSON.stringify(excludedSaveIds) !== JSON.stringify(original.excluded_save_ids || [])) changes.excluded_save_ids = excludedSaveIds;
    if (!Object.keys(changes).length) { cancelEdit(); return; }
    setSaving(true); setError("");
    try { await onSaveTrip(trip.id, changes); cancelEdit(); }
    catch { setError("Could not save your changes. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="trip-detail">
      <div data-testid="trip-top-actions" className="mb-6 flex items-center justify-between gap-3">
        <button onClick={onBack} disabled={busy} data-testid="trip-back-btn" className="flex shrink-0 items-center gap-1.5 text-stone-500 hover:text-stone-900 font-semibold transition-colors disabled:opacity-50">
          <ArrowLeft className="w-4 h-4" /> All trips
        </button>
        {!editing && <div data-testid="trip-export-actions" className="ml-auto flex shrink-0 items-center gap-2"><ItineraryExports trip={trip} disabled={busy} /></div>}
      </div>

      {editing ? <TripInlineFields draft={draft} setDraft={setDraft} saving={saving || placing || reordering} error={error} onUploading={setUploading} startDateParts={startDateParts} onStartDateChange={changeStartDate} /> : <div data-testid="trip-detail-header" className={trip.image_path ? "relative flex items-end rounded-3xl overflow-hidden min-h-48 mb-8" : "relative mb-8"}>
        {trip.image_path && <>
          <img data-testid="trip-detail-cover-image" src={fileUrl(trip.image_path)} alt={trip.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent" />
        </>}
        <div className={trip.image_path ? "relative w-full min-w-0 p-6 text-white" : "min-w-0 text-stone-900"}>
          <div className="itinerary-title-row flex items-baseline justify-between gap-3">
            <h2 data-testid="trip-detail-title" className="min-w-0 flex-1 text-2xl font-black font-['Nunito'] [overflow-wrap:anywhere]">{trip.title}</h2>
            <span data-testid="trip-detail-duration" className={`shrink-0 whitespace-nowrap text-xs sm:text-sm font-medium ${trip.image_path ? "text-white/80" : "text-stone-500"}`}>{formatTripDuration(days.length)}</span>
          </div>
          {trip.destination && <p data-testid="trip-detail-destination" className={`flex items-start gap-1 mt-1 ${trip.image_path ? "text-white/80" : "text-stone-500"}`}><MapPin className="w-4 h-4 shrink-0 mt-1" /><span className="min-w-0 [overflow-wrap:anywhere]">{trip.destination}</span></p>}
        </div>
      </div>}

      <SavedPlaceDragArea onPlace={placeOnDay} onReorder={reorderDays}>
      <ItinerarySavedPlaces places={matchedPlaces} disabled={busy} hasDays={days.length > 0} onOpen={(place) => setOpenedPlace({ id: place.id, title: place.title })} onRemove={(place) => requestDelete({ type: "saved-place", saveId: place.id })} />

      <div className="flex items-center justify-between mb-6">
        <h3 data-testid="trip-timeline-heading" className="text-xl font-bold font-['Nunito'] text-stone-900">Timeline</h3>
        <Button onClick={addDay} disabled={saving || placing || reordering || addingActivity} data-testid="add-day-btn" variant="outline" className="rounded-full font-bold">
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={3} /> Add day
        </Button>
      </div>

      {days.length === 0 ? (
        <p data-testid="trip-timeline-empty" className="text-stone-500">No days yet. Add your first day to build the timeline.</p>
      ) : (
        <div data-testid="trip-timeline" className="border-l-0 sm:border-l-2 border-stone-200 ml-0 sm:ml-4 space-y-8">
          {days.map((day, index) => (
            <DayBlock key={day.id} day={day} dayIndex={index} entries={pendingEntries[day.id] || []} onEntriesChange={(update) => updatePendingEntries(day.id, update)} editing={editing} disabled={saving || placing || reordering || addingActivity} showErrors={!!error} placeStatus={placeStatus?.dayId === day.id ? placeStatus : null} revealedActivity={revealedActivity} allowedEmptyIds={allowedEmptyActivityIds} onRemovePlace={removeActivityPlace} onOpenPlace={(saveId, title) => setOpenedPlace({ id: saveId, title })} onEditActivity={editActivity} onAddActivity={addActivity} onRemoveActivity={(dayId, activityId) => requestDelete({ type: "activity", dayId, activityId })} onRemoveDay={(dayId) => requestDelete({ type: "day", dayId })} />
          ))}
        </div>
      )}
      </SavedPlaceDragArea>
      {reorderError && <p data-testid="itinerary-reorder-error" role="alert" className="mt-4 text-sm text-rose-600">{reorderError}</p>}
      {editing && error && <p data-testid="edit-trip-error" role="alert" className="mt-6 text-sm text-rose-600">{error}</p>}
      <div data-testid="trip-detail-actions" className="flex flex-wrap justify-center gap-2 mt-8 pt-6 border-t border-stone-200">
        {editing ? <>
          <Button type="button" data-testid="edit-trip-cancel" onClick={cancelEdit} disabled={busy} variant="outline" className="flex-1 sm:flex-none rounded-full font-bold">Cancel</Button>
          <Button type="button" data-testid="edit-trip-save" onClick={save} disabled={busy || !draft.title.trim() || parseTripStartDate(startDateParts) === null} className="flex-1 sm:flex-none rounded-full bg-ac hover:bg-ac-hover font-bold">{saving ? "Saving…" : "Save changes"}</Button>
        </> : <>
          <Button type="button" data-testid="edit-trip-btn" onClick={beginEdit} disabled={busy} variant="outline" className="flex-1 sm:flex-none rounded-full font-bold text-stone-700"><Pencil aria-hidden="true" className="w-4 h-4" />Edit</Button>
          <Button type="button" data-testid="delete-trip-btn" onClick={() => requestDelete({ type: "trip" })} disabled={busy} variant="outline" className="flex-1 sm:flex-none rounded-full font-bold text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"><Trash2 aria-hidden="true" className="w-4 h-4" />Delete</Button>
        </>}
      </div>
      <ItineraryPlaceDetails selection={openedPlace} place={(saves || []).find((place) => place.id === openedPlace?.id)} onClose={() => setOpenedPlace(null)} />
      {deletion && <ItineraryDeleteConfirmation target={deletion} editing={editing} deleting={deleting} error={deleteError} onCancel={() => { if (!deleting) setDeletion(null); }} onConfirm={confirmDelete} onCloseAutoFocus={restoreDeleteFocus} />}
    </div>
  );
};

const ItineraryDeleteConfirmation = ({ target, editing, deleting, error, onCancel, onConfirm, onCloseAutoFocus }) => {
  const removingPlace = target.type === "saved-place";
  const subject = target.type === "trip" ? "This trip and all its days and activities"
    : target.type === "day" ? "This day and all its activities" : "This activity";
  const description = removingPlace
    ? `Remove this place from this itinerary’s Saved Places${editing ? " when you select Save changes" : ""}? Your original My Saves entry and activities already added to days stay unchanged.`
    : editing && target.type !== "trip"
      ? `${subject} will be removed from your draft. Deletion is applied only when you select Save changes.`
      : `${subject} will be permanently deleted. This can’t be undone.`;
  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent data-testid="itinerary-delete-confirm-dialog" className="w-[calc(100%-2rem)] max-w-sm rounded-3xl font-['Figtree']" onEscapeKeyDown={(event) => { if (deleting) event.preventDefault(); }} onCloseAutoFocus={onCloseAutoFocus}>
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle data-testid="itinerary-delete-title" className="font-['Nunito']">{removingPlace ? "Remove this saved place?" : `Delete this ${target.type}?`}</AlertDialogTitle>
          <AlertDialogDescription data-testid="itinerary-delete-description">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p data-testid="itinerary-delete-error" role="alert" className="text-sm text-rose-600">{error}</p>}
        <AlertDialogFooter className="flex-row gap-2 sm:space-x-0">
          <AlertDialogCancel data-testid="itinerary-delete-cancel" disabled={deleting} className="mt-0 flex-1 rounded-full font-bold">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="itinerary-delete-confirm" disabled={deleting} onClick={(event) => { event.preventDefault(); onConfirm(); }} className="flex-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white font-bold">{deleting ? (removingPlace ? "Removing…" : "Deleting…") : removingPlace ? "Remove" : "Delete"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const TripInlineFields = ({ draft, setDraft, saving, error, onUploading, startDateParts, onStartDateChange }) => (
  <fieldset data-testid="trip-inline-editor" disabled={saving} className="mb-8 space-y-4 min-w-0">
    <ImageUpload value={draft.image_path} onChange={(path) => setDraft((current) => ({ ...current, image_path: path }))} onUploadStateChange={onUploading} testid="edit-trip-image" />
    <div className="space-y-1.5">
      <Label data-testid="edit-trip-name-label" htmlFor="edit-trip-title-input">Trip name<span data-testid="edit-trip-title-required" aria-hidden="true" className="text-red-600">*</span></Label>
      <div className="flex items-center gap-3">
        <Input id="edit-trip-title-input" data-testid="edit-trip-title-input" aria-required="true" aria-invalid={!!error && !draft.title.trim()} placeholder="Trip name" value={draft.title} onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))} className="min-w-0 flex-1 h-11 rounded-xl font-['Nunito'] font-bold" />
        <span data-testid="trip-detail-duration" className="shrink-0 whitespace-nowrap text-xs sm:text-sm font-medium text-stone-500">{formatTripDuration(draft.days.length)}</span>
      </div>
    </div>
    <div className="space-y-1.5">
      <Label data-testid="edit-trip-places-label" htmlFor="edit-trip-places-input">Places</Label>
      <Input id="edit-trip-places-input" data-testid="edit-trip-places-input" placeholder="Places" value={draft.destination} onChange={(event) => setDraft((current) => ({ ...current, destination: event.target.value }))} className="h-11 rounded-xl" />
    </div>
    <TripStartDateField value={startDateParts} onChange={onStartDateChange} prefix="edit-trip-start" disabled={saving} inputClassName="h-11" />
  </fieldset>
);

const DayBlock = ({ day, dayIndex, entries, onEntriesChange: setEntries, editing, disabled, showErrors, placeStatus, revealedActivity, allowedEmptyIds, onRemovePlace, onOpenPlace, onEditActivity, onAddActivity, onRemoveActivity, onRemoveDay }) => {
  const { setNodeRef } = useDroppable({ id: `itinerary-day-${day.id}`, data: { type: "itinerary-day", dayId: day.id, label: day.label, index: dayIndex, position: 0, empty: day.activities.length === 0 }, disabled });
  const { active, over } = useDndContext();
  const isOver = over?.data.current?.dayId === day.id;
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const addingRef = useRef(false);
  const locked = disabled || adding;
  const dayDrag = useDraggable({ id: `day-content-${day.id}`, data: { type: "day-content", dayId: day.id, label: day.label, preview: day.activities.map((activity) => activity.title || activity.place_links?.map((link) => link.title).join(", ")).filter(Boolean).slice(0, 3).join(" · ") || "Empty day" }, disabled: locked });
  const textRequired = (activity) => !allowedEmptyIds.includes(activity.id) && !activity.save_id && !activity.place_links?.length;
  useEffect(() => { if (!editing) setAddError(""); }, [editing]);

  const add = async (entry) => {
    if (disabled || addingRef.current || !hasActivityContent(entry)) return;
    addingRef.current = true;
    setAdding(true); setAddError("");
    try {
      await onAddActivity(day.id, { id: entry.id, time: entry.time, title: toSentenceCase(entry.title, { preserveCapitalization: true }), description: "", place_links: entry.place_links || [] });
      setEntries((current) => current.filter((row) => row.id !== entry.id));
    } catch {
      setAddError("Could not save this activity. Press Enter to try again.");
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };
  const updateEntry = (id, changes) => setEntries((current) => current.map((entry) => entry.id === id ? { ...entry, ...changes } : entry));
  const removeEntryPlace = (entry, saveId) => {
    updateEntry(entry.id, { place_links: (entry.place_links || []).filter((link) => link.save_id !== saveId) });
    requestAnimationFrame(() => document.getElementById(`itinerary-entry-${entry.id}`)?.querySelector('input[aria-label="New activity text"]')?.focus({ preventScroll: true }));
  };
  const submitEntry = (event, entry) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (!event.repeat) add(entry);
  };

  return (
    <div ref={setNodeRef} className={`relative pl-0 sm:pl-8 pb-2 ${isOver ? "rounded-xl bg-ac-soft ring-2 ring-ac" : ""}`} data-testid={`day-block-${day.id}`} data-drop-over={isOver}>
      <span data-testid={`day-dot-${day.id}`} aria-hidden="true" className="hidden sm:block absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-ac border-4 border-white" />
      <div className="flex items-center justify-between gap-2 mb-3">
        <div ref={dayDrag.setNodeRef} {...dayDrag.attributes} {...dayDrag.listeners} data-testid={`day-drag-${day.id}`} aria-label={`Hold to swap the contents of ${day.label || "this day"}`} onContextMenu={(event) => event.preventDefault()} style={{ touchAction: "pan-y", userSelect: "none", WebkitTouchCallout: "none" }} className={`flex flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0 ${locked ? "" : "cursor-grab active:cursor-grabbing"}`}>
          <p data-testid={`day-label-${day.id}`} className="shrink-0 font-bold font-['Nunito'] text-stone-900">{day.label || "Day"}{day.date ? " -" : ""}</p>
          {day.date && <p data-testid={`day-date-${day.id}`} className="text-stone-900 font-semibold"><span data-testid={`day-date-mobile-${day.id}`} className="sm:hidden">{formatTripDayDate(day.date, "short")}</span><span data-testid={`day-date-desktop-${day.id}`} className="hidden sm:inline">{formatTripDayDate(day.date)}</span></p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button type="button" onClick={() => setEntries((current) => [...current, { id: uid(), time: "", title: "" }])} disabled={locked} data-testid={`add-activity-${day.id}`} aria-label="Add activity" variant="outline" size="icon" className="h-9 w-9 shrink-0 rounded-xl border-stone-300 bg-white text-stone-700 hover:border-stone-400 hover:bg-white hover:text-stone-900 transition-colors"><Plus className="w-4 h-4" strokeWidth={2.5} /></Button>
          <button type="button" disabled={disabled} onClick={() => onRemoveDay(day.id)} data-testid={`remove-day-${day.id}`} aria-label={`Remove ${day.label || "day"}`} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-stone-300 bg-white text-stone-700 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors disabled:opacity-50">
            <Trash2 className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {placeStatus && <p id={`itinerary-place-status-${day.id}`} data-testid="itinerary-place-status" tabIndex={-1} role={placeStatus.error ? "alert" : "status"} className={`mb-3 rounded text-sm break-words focus:outline-none ${placeStatus.error ? "text-rose-600" : "text-stone-600"}`}>{placeStatus.text}</p>}
      {isOver && active?.data.current?.place && over?.data.current?.type === "itinerary-day" && !day.activities.length && <div data-testid={`activity-insertion-marker-${day.id}`} className="pointer-events-none relative z-10 h-0"><div className="absolute inset-x-0 h-0.5 bg-ac"><span data-testid="activity-insertion-label" className="absolute left-2 bottom-0.5 whitespace-nowrap rounded bg-ac px-2 py-0.5 text-xs font-semibold text-white">Add here · {day.label}</span></div></div>}
      <div className={`space-y-2 ${dayDrag.isDragging ? "opacity-40" : ""}`}>
        {day.activities.map((a, index) => (
          <ItineraryActivityDrag key={a.id} day={day} activity={a} position={index} disabled={locked} editing={editing} id={`itinerary-activity-${a.id}`} data-testid={`activity-${a.id}`} data-drop-feedback={revealedActivity?.id === a.id ? revealedActivity.kind : undefined} className={`group relative scroll-my-6 focus:outline-none ${revealedActivity?.id === a.id ? "ring-2 ring-ac" : ""} ${editing ? "grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[6rem_minmax(0,1fr)_auto] gap-2" : "flex gap-3"} items-start bg-white border border-stone-200 rounded-2xl px-4 py-3 hover:border-stone-300 transition-colors`}>
            <ActivityContentTarget day={day} dayIndex={dayIndex} position={index} activityId={a.id} disabled={locked} />
            <ActivityInsertionTarget day={day} dayIndex={dayIndex} position={index} edge="before" disabled={locked} />
            <ActivityInsertionTarget day={day} dayIndex={dayIndex} position={index + 1} edge="after" disabled={locked} />
            {revealedActivity?.id === a.id && <span data-testid={`activity-drop-feedback-${a.id}`} className="absolute -top-2 right-3 rounded-full bg-ac-soft px-2 text-xs font-semibold text-ac-softfg">{revealedActivity.kind === "duplicate" ? "Already added" : revealedActivity.kind === "linked" ? "Place added" : revealedActivity.kind === "removed" ? "Place removed" : "Added"}</span>}
            {editing ? <>
              <Input data-testid={`edit-activity-time-${a.id}`} aria-label="Activity time" placeholder="09:00" value={a.time || ""} disabled={disabled} onChange={(event) => onEditActivity(day.id, a.id, { time: event.target.value })} className="w-24 sm:w-full min-w-0 h-11 rounded-xl" />
              <div className="col-span-2 row-start-2 sm:col-span-1 sm:row-start-1 sm:col-start-2 min-w-0">
                <Textarea data-testid={`edit-activity-title-${a.id}`} aria-label="Activity text" aria-required={textRequired(a)} aria-invalid={showErrors && textRequired(a) && !a.title.trim()} placeholder={a.place_links?.length ? "" : "Activity"} value={a.title} disabled={disabled} rows={2} onChange={(event) => onEditActivity(day.id, a.id, { title: event.target.value })} className={`min-w-0 min-h-[44px] rounded-xl ${showErrors && textRequired(a) && !a.title.trim() ? "border-rose-400" : ""}`} />
                {!!a.place_links?.length && <div className="mt-2"><ActivityPlaceChips links={a.place_links} prefix={`activity-${a.id}`} disabled={disabled} onOpen={onOpenPlace} onRemove={(saveId) => onRemovePlace(day.id, a.id, saveId)} /></div>}
              </div>
            </> : <>
              {a.time && <span data-testid={`activity-time-display-${a.id}`} className="flex items-center gap-1 text-xs font-bold text-ac bg-ac-soft rounded-full px-2.5 py-1 mt-0.5 whitespace-nowrap"><Clock className="w-3 h-3" /> {a.time}</span>}
              <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                {a.save_id ? <ActivityPlaceChips links={[{ save_id: a.save_id, title: a.title || "Saved place" }]} prefix={`activity-${a.id}-legacy`} disabled={disabled} onOpen={onOpenPlace} onRemove={(saveId) => onRemovePlace(day.id, a.id, saveId)} /> : <span data-testid={`activity-title-display-${a.id}`} className="text-stone-700">{a.title}</span>}
                {a.title && !!a.place_links?.length && " "}<ActivityPlaceChips links={a.place_links} prefix={`activity-${a.id}`} disabled={disabled} onOpen={onOpenPlace} onRemove={(saveId) => onRemovePlace(day.id, a.id, saveId)} />
              </div>
            </>}
            <button type="button" disabled={disabled} onClick={() => onRemoveActivity(day.id, a.id)} data-testid={`remove-activity-${a.id}`} aria-label="Remove activity" className={`${editing ? "col-start-2 row-start-1 sm:col-start-3 grid place-items-center w-8 h-11" : "opacity-0 group-hover:opacity-100"} text-stone-300 hover:text-rose-500 transition-colors disabled:opacity-50`}>
              <Trash2 className="w-4 h-4" />
            </button>
            {editing && a.save_id && <div className="col-span-2 sm:col-span-3 min-w-0"><ActivityPlaceChips links={[{ save_id: a.save_id, title: a.title || "Saved place" }]} prefix={`activity-${a.id}-legacy`} disabled={disabled} onOpen={onOpenPlace} onRemove={(saveId) => onRemovePlace(day.id, a.id, saveId)} /></div>}
          </ItineraryActivityDrag>
        ))}
      </div>

      {entries.map((entry, index) => {
        const inputId = index === 0 ? day.id : `${day.id}-${entry.id}`;
        return <div key={entry.id} id={`itinerary-entry-${entry.id}`} data-testid={`activity-entry-${entry.id}`} className={`relative flex items-start gap-2 mt-3 rounded-xl ${revealedActivity?.entryId === entry.id ? "ring-2 ring-ac" : ""}`}>
          <ActivityContentTarget day={day} dayIndex={dayIndex} position={day.activities.length + index} entryId={entry.id} disabled={locked} />
          <Input data-testid={`activity-time-${inputId}`} disabled={locked} aria-label="New activity time" placeholder="09:00" value={entry.time} onChange={(event) => updateEntry(entry.id, { time: event.target.value })} onKeyDown={(event) => submitEntry(event, entry)} className="rounded-xl w-24 shrink-0" />
          <div className="min-w-0 flex-1">
            <Input data-testid={`activity-title-${inputId}`} disabled={locked} autoFocus aria-label="New activity text" placeholder={entry.place_links?.length ? "" : "Add an activity…"} value={entry.title} onChange={(event) => updateEntry(entry.id, { title: event.target.value })} onKeyDown={(event) => submitEntry(event, entry)} className="rounded-xl min-w-0" />
            {!!entry.place_links?.length && <div className="mt-2"><ActivityPlaceChips links={entry.place_links} prefix={`pending-${entry.id}`} disabled={locked} onOpen={onOpenPlace} onRemove={(saveId) => removeEntryPlace(entry, saveId)} /></div>}
          </div>
        </div>;
      })}
      {addError && <p data-testid={`activity-add-error-${day.id}`} role="alert" className="mt-2 text-sm text-rose-600">{addError}</p>}
    </div>
  );
};
